import { Form, Modal, Spin, Typography } from "antd";
import axios, { isCancel } from "axios";
import { useEffect, useState } from "react";

import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import { METADATA_FIELDS_ENDPOINT_PATH, TAGS_API_BASE } from "../helper";
import MetadataFields from "../MetadataFields";

const { Text } = Typography;

/**
 * Views and edits the extra fields one mapping's own product line records
 * against it.
 *
 * Opened per row rather than shown as columns, because the product-wide screen
 * spans several product lines at once and no single set of headings would mean
 * the same thing on every row.
 *
 * The field list comes from the parent's per-type cache when it holds one.
 * Otherwise the modal fetches it and shows a spinner until the fetch
 * completes, so "no extra fields" is only ever shown for a list that loaded
 * empty. The form mounts once the list is known and takes the row's metadata
 * as its initial values.
 *
 * Saving sends the row's current decision back unchanged alongside the
 * metadata, so editing a field here never quietly re-decides the mapping.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 * @param {object} props.tag - The row whose metadata is being edited.
 * @param {Array<{name: string, label: string, kind: string, options: Array<string>}>} [props.cachedMetadataFields]
 *   Field list of the row's type, absent when the parent has not loaded it.
 * @param {boolean} props.hasManagementAccess
 * @param {() => void} props.onClose
 * @param {() => void} props.onSaved
 */
const MetadataModal = ({ ROOT_URL, tag, cachedMetadataFields, hasManagementAccess, onClose, onSaved }) => {
  const [fetchedMetadataFields, setFetchedMetadataFields] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const { uniquePublicId, tagDomain, tagDomainDisplayName, sourceTag, canonicalTag, isReviewed, metadata } = tag;
  const metadataFields = cachedMetadataFields ?? fetchedMetadataFields;
  const areFieldsLoading = metadataFields === null || metadataFields === undefined;

  /**
   * Saves the edited metadata, sending the row's current decision back
   * unchanged. Closes through `onSaved` on success.
   *
   * @param {Record<string, any>} formValues - Metadata field values keyed by field name.
   */
  const handleSubmit = (formValues) => {
    setIsSaving(true);
    axios
      .patch(`${ROOT_URL}/${TAGS_API_BASE}/${uniquePublicId}`, {
        canonicalTag,
        isReviewed,
        metadata: formValues
      })
      .then(() => {
        showSuccessNotification("Metadata saved successfully!");
        onSaved();
      })
      .catch(showErrorNotification)
      .finally(() => setIsSaving(false));
  };

  useEffect(() => {
    if (cachedMetadataFields) {
      return undefined;
    }

    const abortController = new AbortController();
    axios
      .get(`${ROOT_URL}/${TAGS_API_BASE}/${METADATA_FIELDS_ENDPOINT_PATH}`, {
        signal: abortController.signal,
        params: { tagDomain }
      })
      .then((response) => setFetchedMetadataFields(response.data.metadataFields))
      .catch((error) => {
        if (isCancel(error)) {
          return;
        }

        showErrorNotification(error);
      });

    return () => abortController.abort();
  }, [ROOT_URL, tagDomain, cachedMetadataFields]);

  return (
    <Modal
      title="Mapping metadata"
      open
      onCancel={onClose}
      okText="Save metadata"
      okButtonProps={{ disabled: !hasManagementAccess || areFieldsLoading || metadataFields.length === 0 }}
      confirmLoading={isSaving}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        {tagDomainDisplayName} &middot; {sourceTag || canonicalTag}
      </Text>
      {areFieldsLoading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <Spin />
        </div>
      )}
      {!areFieldsLoading && metadataFields.length === 0 && (
        <Text>This type records no extra fields against a mapping.</Text>
      )}
      {!areFieldsLoading && metadataFields.length > 0 && (
        <Form form={form} layout="vertical" initialValues={metadata} onFinish={handleSubmit}>
          <MetadataFields metadataFields={metadataFields} isDisabled={!hasManagementAccess} />
        </Form>
      )}
    </Modal>
  );
};

export default MetadataModal;
